import React, { useMemo } from 'react';
import {
  Button,
  Grid,
  MultiSelect,
  Select,
  Stack,
  TextInput,
  Group,
  Paper,
  Flex,
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { useTranslation } from 'react-i18next';
import { IconSearch, IconX } from '@tabler/icons-react';
import { ISearchAuditLogsFilter, IAuditLogMetadata } from '../../../global/entities/SystemAuditLog/SystemAuditLog';

interface AuditLogFiltersProps {
  filter: ISearchAuditLogsFilter;
  metadata: IAuditLogMetadata | undefined;
  isLoading: boolean;
  onFilterChange: (filter: ISearchAuditLogsFilter) => void;
  onSearch: () => void;
  onClearFilters: () => void;
}

export const AuditLogFilters: React.FC<AuditLogFiltersProps> = ({
  filter,
  metadata,
  isLoading,
  onFilterChange,
  onSearch,
  onClearFilters,
}) => {
  const { t } = useTranslation();

  const categoryOptions = useMemo(
    () =>
      metadata?.categories?.map((c) => ({
        value: c,
        label: c,
      })) || [],
    [metadata?.categories]
  );

  const actionOptions = useMemo(
    () =>
      metadata?.actions?.map((a) => ({
        value: a,
        label: a,
      })) || [],
    [metadata?.actions]
  );

  const entityTypeOptions = useMemo(
    () =>
      metadata?.entityTypes?.map((e) => ({
        value: e,
        label: e,
      })) || [],
    [metadata?.entityTypes]
  );

  const actorTypeOptions = useMemo(
    () =>
      metadata?.actorTypes?.map((at) => ({
        value: at,
        label: at,
      })) || [],
    [metadata?.actorTypes]
  );

  return (
    <Paper p="md" radius="md" withBorder>
      <Stack gap="md">
        <Grid>
          <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
            <DatePickerInput
              label={t('auditLog.filter.fromDate')}
              placeholder={t('auditLog.filter.fromDatePlaceholder')}
              value={filter.fromDate ? new Date(filter.fromDate) : null}
              onChange={(date) =>
                onFilterChange({
                  ...filter,
                  fromDate: date ? date.toISOString().split('T')[0] : undefined,
                  pageNumber: 1,
                })
              }
              clearable
              disabled={isLoading}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
            <DatePickerInput
              label={t('auditLog.filter.toDate')}
              placeholder={t('auditLog.filter.toDatePlaceholder')}
              value={filter.toDate ? new Date(filter.toDate) : null}
              onChange={(date) =>
                onFilterChange({
                  ...filter,
                  toDate: date ? date.toISOString().split('T')[0] : undefined,
                  pageNumber: 1,
                })
              }
              clearable
              disabled={isLoading}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
            <MultiSelect
              label={t('auditLog.filter.category')}
              placeholder={t('auditLog.filter.categoryPlaceholder')}
              data={categoryOptions}
              value={filter.category ? [filter.category] : []}
              onChange={(values) =>
                onFilterChange({
                  ...filter,
                  category: values.length > 0 ? values[0] : undefined,
                  pageNumber: 1,
                })
              }
              searchable
              clearable
              disabled={isLoading}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
            <MultiSelect
              label={t('auditLog.filter.action')}
              placeholder={t('auditLog.filter.actionPlaceholder')}
              data={actionOptions}
              value={filter.action ? [filter.action] : []}
              onChange={(values) =>
                onFilterChange({
                  ...filter,
                  action: values.length > 0 ? values[0] : undefined,
                  pageNumber: 1,
                })
              }
              searchable
              clearable
              disabled={isLoading}
            />
          </Grid.Col>
        </Grid>

        <Grid>
          <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
            <Select
              label={t('auditLog.filter.entityType')}
              placeholder={t('auditLog.filter.entityTypePlaceholder')}
              data={entityTypeOptions}
              value={filter.entityType || ''}
              onChange={(value) =>
                onFilterChange({
                  ...filter,
                  entityType: value || undefined,
                  pageNumber: 1,
                })
              }
              searchable
              clearable
              disabled={isLoading}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
            <TextInput
              label={t('auditLog.filter.entityId')}
              placeholder={t('auditLog.filter.entityIdPlaceholder')}
              value={filter.entityId ? filter.entityId.toString() : ''}
              onChange={(e) =>
                onFilterChange({
                  ...filter,
                  entityId: e.currentTarget.value ? parseInt(e.currentTarget.value) : undefined,
                  pageNumber: 1,
                })
              }
              type="number"
              disabled={isLoading}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
            <TextInput
              label={t('auditLog.filter.actorUserId')}
              placeholder={t('auditLog.filter.actorUserIdPlaceholder')}
              value={filter.actorUserId ? filter.actorUserId.toString() : ''}
              onChange={(e) =>
                onFilterChange({
                  ...filter,
                  actorUserId: e.currentTarget.value ? parseInt(e.currentTarget.value) : undefined,
                  pageNumber: 1,
                })
              }
              type="number"
              disabled={isLoading}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
            <Select
              label={t('auditLog.filter.actorType')}
              placeholder={t('auditLog.filter.actorTypePlaceholder')}
              data={actorTypeOptions}
              value={filter.actorType || ''}
              onChange={(value) =>
                onFilterChange({
                  ...filter,
                  actorType: value || undefined,
                  pageNumber: 1,
                })
              }
              searchable
              clearable
              disabled={isLoading}
            />
          </Grid.Col>
        </Grid>

        <Group justify="flex-end" gap="sm">
          <Button
            variant="light"
            leftSection={<IconX size={16} />}
            onClick={onClearFilters}
            disabled={isLoading}
          >
            {t('button.clearFilters')}
          </Button>
          <Button
            leftSection={<IconSearch size={16} />}
            onClick={onSearch}
            loading={isLoading}
          >
            {t('button.search')}
          </Button>
        </Group>
      </Stack>
    </Paper>
  );
};
